import { useState } from 'react';
import { useSelector } from 'react-redux';
import { notification, Row, Col, Spin, Form, Input, Select, Button } from 'antd';
import { useI18nLocalization } from '../../i18nLocalization/useI18nLocalization';
import { useCompanySettingsLogic } from '../useCompanySettingsLogic';
import PageContent from '../../structure/PageContent';

const CompanyProfile = () => {
	const company = useSelector((state) => state.company);
	const { availableLocalesAsSortedArray, t } = useI18nLocalization();
	const companySettingsLogic = useCompanySettingsLogic();

	const [form] = Form.useForm();
	const [isSaving, setIsSaving] = useState(false);

	const getCompanySettings = (formValues) => {
		const companySettingsObj = {
			enterpriseProfileId: company.enterpriseProfileId,
			homeLocale: formValues.homeLocale
		};
		return companySettingsObj;
	};

	const handleOnSave = async (formValues) => {
		setIsSaving(true);
		const companySettingsToUpdate = getCompanySettings(formValues);

		const successful = await companySettingsLogic.updateCompanySettings(companySettingsToUpdate);
		if (successful) {
			notification['success']({
				message: t(`Settings saved!`)
			});
			setIsSaving(false);
			clearForm();
		} else {
			notification['error']({
				message: `We encountered an error saving the settings. Please try again. If it persists, please submit an EntDev Helpdesk ticket.`,
				duration: 10
			});
			setIsSaving(false);
		}
	};

	const clearForm = () => {
		form.resetFields();
	};

	return !company ? (
		<Row justify='center' align='middle' style={{ height: '100%' }}>
			<Col>
				<Spin />
			</Col>
		</Row>
	) : (
		<>
			<PageContent>
				<Form form={form} layout='vertical' scrollToFirstError onFinish={handleOnSave}>
					<Row gutter={[16, 0]}>
						<Col span={24} lg={{ span: 12 }}>
							<Form.Item label={t('Enterprise Profile Id')}>
								<Input
									disabled
									className='darken-disabled-text'
									placeholder='enterprise profile id'
									value={company.enterpriseProfileId}
								/>
							</Form.Item>
						</Col>
						<Col span={24} lg={{ span: 12 }}>
							<Form.Item label={t('Company')}>
								<Input disabled className='darken-disabled-text' placeholder='company' value={company.company} />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={[16, 0]}>
						<Col span={24} lg={{ span: 12 }}>
							<Form.Item label={t('Profile')}>
								<Input disabled className='darken-disabled-text' placeholder='profile' value={company.profile} />
							</Form.Item>
						</Col>
						<Col span={24} lg={{ span: 12 }}>
							<Form.Item label={t('Display Name')}>
								<Input
									disabled
									className='darken-disabled-text'
									placeholder='display name'
									value={company.displayName}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Form.Item label={t('Office Location')}>
						<Input disabled className='darken-disabled-text' value={company.officeLocation} />
						<span className='form-field-help-text'>
							{t('Some enterprise profiles may have this intentionally left blank')}
						</span>
					</Form.Item>
					<Form.Item label={t('Email Domain')}>
						<Input disabled className='darken-disabled-text' value={company.emailDomain} />
					</Form.Item>
					<Form.Item
						name='homeLocale'
						label={t('Home Locale')}
						rules={[{ required: true, message: t('Home Locale is required') }]}
						initialValue={company.settings.settings.homeLocale}
					>
						<Select placeholder={t('choose profile')} value={company.settings.settings.homeLocale}>
							{_.map(availableLocalesAsSortedArray, ({ id, name }) => {
								return (
									<Select.Option key={id} value={id}>
										{name}
									</Select.Option>
								);
							})}
						</Select>
					</Form.Item>
					<Row>
						<Col flex='auto' style={{ textAlign: 'right' }}>
							<Form.Item>
								<Button type='primary' htmlType='submit' loading={isSaving}>
									{t('Save')}
								</Button>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</PageContent>
		</>
	);
};

export default CompanyProfile;
